import { useState, useEffect, useCallback } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import RequestDetail from "../components/RequestDetail";
import requestService from "../../../services/requestService";
import { useAuth } from "../../../shared/context/AuthContext";

const mapApiRequestDetail = (r) => ({
  id: r.id,
  assetId: r.assetId,
  requestNumber: r.requestNumber,
  title: r.workOrderName,
  priority: r.criticality,
  description: r.workOrderDescription,
  asset: r.assetName,
  assignedTo: r.assignedTo,
  dueDate: r.dueDate
    ? new Date(r.dueDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—",
  createdDate: "—",
  cost: r.totalPartsCost ?? 0,
  parts: r.spareParts?.length ?? 0,
  partsList: (r.spareParts ?? []).map((p) => ({
    name: p.sparePartName,
    quantity: p.quantityRequired,
    cost: p.partsCost,
  })),
});

const RequestDetailSkeleton = () => (
  <Box sx={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "12px", padding: { xs: "20px", md: "32px" }, display: "flex", flexDirection: "column", gap: 2 }}>
    {/* Header */}
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="text" width={240} height={28} />
      </Box>
      <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: "4px" }} />
    </Box>
    {/* Breadcrumb */}
    <Skeleton variant="text" width={160} height={20} />
    {/* Description */}
    <Skeleton variant="text" width="100%" height={20} />
    <Skeleton variant="text" width="80%" height={20} />
    {/* Info cards row 1 */}
    <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
      <Skeleton variant="rounded" sx={{ flex: 1, height: 72, borderRadius: "12px" }} />
      <Skeleton variant="rounded" sx={{ flex: 1, height: 72, borderRadius: "12px" }} />
    </Box>
    {/* Info cards row 2 */}
    <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
      <Skeleton variant="rounded" sx={{ flex: 1, height: 72, borderRadius: "12px" }} />
      <Skeleton variant="rounded" sx={{ flex: 1, height: 72, borderRadius: "12px" }} />
    </Box>
    {/* Cost + Parts */}
    <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
      <Skeleton variant="rounded" width={280} height={100} sx={{ borderRadius: "12px" }} />
      <Skeleton variant="rounded" sx={{ flex: 1, height: 100, borderRadius: "12px" }} />
    </Box>
    {/* Comment box */}
    <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: "8px" }} />
    {/* Buttons */}
    <Box sx={{ display: "flex", gap: 2 }}>
      <Skeleton variant="rounded" sx={{ flex: 1, height: 48, borderRadius: "8px" }} />
      <Skeleton variant="rounded" sx={{ flex: 1, height: 48, borderRadius: "8px" }} />
    </Box>
  </Box>
);

export default function RequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestService.getRequestById(requestId);
      const data = res.data.data;
      const item = Array.isArray(data) ? data[0] : data;
      setRequest(mapApiRequestDetail(item));
    } catch (err) {
      setError(err.message || "Failed to load request.");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const buildComment = (id, commentText) =>
    commentText
      ? {
          workOrderId: id,
          assetId: request.assetId,
          commentText,
          createdBy: user?.username || "",
          createdByRole: user?.role || "",
        }
      : null;

  const handleApprove = (id, commentText) => {
    requestService.updateRequestStatus(id, "approved", buildComment(id, commentText))
      .then(() => navigate("/requests"))
      .catch(() => {});
  };

  const handleReject = (id, commentText) => {
    requestService.updateRequestStatus(id, "rejected", buildComment(id, commentText))
      .then(() => navigate("/requests"))
      .catch(() => {});
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 2.5, md: 3, lg: 4 } }}>
        {loading ? (
          <RequestDetailSkeleton />
        ) : error ? (
          <Typography sx={{ color: "#C10007", fontSize: "14px" }}>{error}</Typography>
        ) : request ? (
          <RequestDetail
            request={request}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ) : (
          <Typography sx={{ color: "#62748E", fontSize: "16px" }}>
            Request not found.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
